import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import * as indexedDBService from '../utils/indexedDBService';
import InvoiceService from '../services/invoiceService';
import { Client } from '../types/client';
import { EmailService } from '../utils/emailService';
import { generateInvoicePDF } from '../utils/invoicePdfGenerator';
import { getTenantDataService } from '../services/tenant-data-service';

export const useZeroClickAutomation = () => {
  const { currentUser, userProfile } = useAuth();
  const hasRun = useRef(false);

  useEffect(() => {
    const runAutomation = async () => {
      if (!currentUser || hasRun.current) return;
      hasRun.current = true; // Prevent running multiple times per session

      try {
        const clients = await indexedDBService.getAllItems<Client>('clients');
        const invoices = await indexedDBService.getAllItems<any>('invoices');
        
        let businessBankDetails = {};
        let userBusinessName = 'Us';
        let smtpSettings = undefined;
        let userBusinessProfile: any = {};
        try {
          const businesses = await indexedDBService.getAllItems<any>('businesses');
          const userBusiness = businesses.find(b => b.userId === currentUser.uid);
          if (userBusiness) {
            userBusinessProfile = userBusiness;
            userBusinessName = userBusiness.name || 'Us';
            if (userBusiness.bankDetails) {
              businessBankDetails = userBusiness.bankDetails;
            }
          }
          
          const settings = await indexedDBService.getItemById<any>('userSettings', currentUser.uid);
          if (settings) {
            if (settings.smtpSettings) {
              smtpSettings = settings.smtpSettings;
            }
            if (settings.businessProfile) {
              userBusinessProfile = {
                ...userBusinessProfile,
                ...settings.businessProfile
              };
              if (settings.businessProfile.bankDetails) {
                businessBankDetails = settings.businessProfile.bankDetails;
              }
            }
          }
        } catch (e) {
          console.error('Failed to load business data for zero-click automation', e);
        }

        for (const client of clients) {
          if (client.isZeroClickEnabled && client.defaultItems && client.defaultItems.length > 0) {
            
            const lastInvoice = invoices
              .filter(inv => inv.clientId === client.id)
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

            let shouldGenerate = false;
            const now = new Date();

            if (!lastInvoice) {
              shouldGenerate = true;
            } else {
              const lastDate = new Date(lastInvoice.date);
              const diffTime = Math.abs(now.getTime() - lastDate.getTime());
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

              if (client.zeroClickFrequency === 'Weekly' && diffDays >= 7) {
                shouldGenerate = true;
              } else if (client.zeroClickFrequency === 'Monthly' && diffDays >= 30) {
                shouldGenerate = true;
              } else if (client.zeroClickFrequency === 'Yearly' && diffDays >= 365) {
                shouldGenerate = true;
              }
            }

            if (shouldGenerate) {
              console.log(`[Zero-Click] Generating invoice for ${client.name}...`);
              
              const tenantDb = getTenantDataService(currentUser, userProfile);
              const inventoryData = await tenantDb.getAll('inventory');
              let missingStock = false;
              let missingItemName = '';
              for (const item of client.defaultItems) {
                const qty = item.quantity || 1;
                const invId = item.inventoryItemId || item.id;
                if (invId) {
                  const invItem = inventoryData.find((i: any) => i.id === invId);
                  if (invItem) {
                     const currentStock = invItem.stockQuantity !== undefined ? invItem.stockQuantity : invItem.stock;
                     if (currentStock !== undefined && Number(currentStock) < qty) {
                       missingStock = true;
                       missingItemName = item.name;
                       break;
                     }
                  }
                }
              }

              if (missingStock) {
                console.warn(`[Zero-Click] Skipping ${client.name} due to insufficient inventory stock for ${missingItemName}.`);
                alert(`Zero-Click Automation paused for ${client.name}: Insufficient stock for ${missingItemName}.`);
                continue; // Skip this client
              }

              const invoiceNumber = await InvoiceService.generateInvoiceNumber(
                currentUser.uid,
                'AUTO'
              );

              const taxRate = 18;
              let totalSubtotal = 0;
              let totalTaxAmount = 0;

              const items = client.defaultItems.map((item, index) => {
                const qty = item.quantity || 1;
                const price = item.price || 0;
                const amount = qty * price;
                const taxAmount = (amount * taxRate) / 100;
                
                totalSubtotal += amount;
                totalTaxAmount += taxAmount;

                return {
                  id: Date.now() + index,
                  name: item.name,
                  description: `Auto-generated via Zero-Click (${client.zeroClickFrequency})`,
                  hsnCode: '',
                  quantity: qty,
                  rate: price,
                  gstRate: taxRate,
                  discount: 0,
                  amount: amount,
                  taxAmount: taxAmount,
                  total: amount + taxAmount,
                  inventoryItemId: item.inventoryItemId || item.id,
                };
              });

              const grandTotal = totalSubtotal + totalTaxAmount;

              const invoiceData = {
                userId: currentUser.uid,
                invoiceNumber,
                date: now.toISOString().split('T')[0],
                dueDate: new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                client: client.name,
                clientId: client.id,
                clientName: client.name,
                clientDetails: {
                    address: client.address || '',
                    gstin: client.gstin || '',
                    email: client.email || '',
                    phone: client.phone || '',
                    state: client.state || '',
                },
                items: items,
                subtotal: totalSubtotal,
                tax: totalTaxAmount,
                discount: 0,
                amount: grandTotal,
                taxType: 'gst',
                taxRate: taxRate,
                discountType: 'fixed',
                discountValue: 0,
                taxable: true,
                status: 'pending' as 'pending',
                notes: 'Thank you for your business. This is an auto-generated invoice.',
                termsAndConditions: 'Payment is due within 15 days.',
                createdAt: now.toISOString(),
                updatedAt: now.toISOString(),
                bankDetails: businessBankDetails,
              };

              await InvoiceService.createInvoice(invoiceData, currentUser.uid);
              console.log(`[Zero-Click] Successfully generated invoice #${invoiceNumber} for ${client.name}`);

              // Auto-send email if enabled
              if (client.autoSendEmail && client.email) {
                try {
                  const subject = `Invoice #${invoiceNumber} from ${userBusinessName}`;
                  const body = `Dear ${client.name},\n\nPlease find the details for Invoice #${invoiceNumber} for the amount of ₹${grandTotal.toFixed(2)}.\n\nThank you for your business.`;
                  const pdfBytes = await generateInvoicePDF(invoiceData, userBusinessProfile, 1);
                  await EmailService.sendInvoiceEmail(client.email, subject, body, pdfBytes, `Invoice_${invoiceNumber}.pdf`, smtpSettings);
                  console.log(`[Zero-Click] Successfully emailed invoice #${invoiceNumber} to ${client.name}`);
                } catch (emailError) {
                  console.error(`[Zero-Click] Failed to auto-send email to ${client.name}`, emailError);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error running Zero-Click automation:', error);
      }
    };

    runAutomation();
  }, [currentUser]);
};
